
// Bootstrap file - this processes the data in __ct_datasets requesting the required files
// from the CDN and also the dataset file for configuration of the DT / E. This file should
// only be loaded once on the page, but it needs to expose `__ct_bootstrap` with a `run`
// function to run a queue.

// There is also a `__ct_style` global created in this file for styling information.

export interface IQueue {
	accessToken: string;
	apiKey: string;
	clientId: string;
	clientName: string;
	condition: string; // actually JSON
	datasetId: string;
	duration: number;
	insertPoint: HTMLElement;
	language: string | null;
	loadUrl: string;
	requiredLibs: {
		[lib: string]: string; // version
	};
	role: string;
	stylingLibs: {
		[lib: string]: string; // version
		_action: string;
	};
	tableId: string;
	viewType: 'table'; // calendar and list in future
}

interface ITestElement extends HTMLDivElement {
	__bm: boolean;
	__bs: boolean;
	__bs4: boolean;
	__bs5: boolean;
	__foundation: boolean;
	__jui: boolean;
	__seUI: boolean;
}

interface IWindow extends Window {
	JSZip?: any;
	Pickr?: any;
	__ct_bootstrap?: any;
	__ct_datasets?: IQueue[];
	__ct_style?: string;
	bootstrap?: any;
	io?: any;
	jQuery?: any;
	moment?: any;
	pdfmake?: any;
}

const win = window as IWindow;
const styleTestEl = document.createElement('div') as ITestElement;
const requested = { // Keep track of which libraries have been requested
	css: new Map<string, boolean>(),
	js: new Map<string, boolean>(),
};
let warningShown = false;
let dtWarningShown = false;

// Functions return `true` if already loaded, `false` if not
// Note that for the `type` parameter we only 'care' about it for the styling frameworks.
// While we could try to see if the DataTables extensions CSS is already loaded, it's
// safe to assume the user won't have loaded it already if the JS isn't present.
const libs = {
	b(type) {
		// JS check only
		return libs.dt(type) && win.jQuery.fn.dataTable.Buttons;
	},
	b_colvis(type) {
		// JS check only
		return libs.b(type) && win.jQuery.fn.dataTable.ext.buttons.colvis;
	},
	b_html5(type) {
		// JS check only
		return libs.b(type) && win.jQuery.fn.dataTable.ext.buttons.pdfHtml5;
	},
	b_print(type) {
		// JS check only
		return libs.b(type) && win.jQuery.fn.dataTable.ext.buttons.print;
	},
	bm(type) {
		if (type === 'bulma') {
			// There is no Bulma JS
			return false;
		}
		else { // type === 'css'
			if (styleTestEl.__bm === undefined) {
				// Tag box
				styleTestEl.className = 'tag is-black';
				let styles = getTestStyles();

				let res = styles.display === 'inline-flex'
					? true
					: false;

				styleTestEl.__bm = res;
			}

			return styleTestEl.__bm;
		}
	},
	bs(type) {
		if (type === 'js') {
			return win.jQuery && win.jQuery.fn.tooltip && win.jQuery.fn.tooltip.Constructor.VERSION && win.jQuery.fn.tooltip.Constructor.VERSION[0] === '3'
				? true
				: false;
		}
		else { // type === 'css'
			if (styleTestEl.__bs === undefined) {
				styleTestEl.className = 'well'; // wells are unquie to bs3
				let styles = getTestStyles();

				let res = styles.minHeight && styles.borderTopWidth
					? true
					: false;

				styleTestEl.__bs = res;
			}

			return styleTestEl.__bs;
		}
	},
	bs4(type) {
		if (type === 'js') {
			return win.jQuery && win.jQuery.fn.tooltip && win.jQuery.fn.tooltip.Constructor.VERSION && win.jQuery.fn.tooltip.Constructor.VERSION[0] === '4'
				? true
				: false;
		}
		else { // type === 'css'
			if (styleTestEl.__bs4 === undefined) {
				// bs3 also has jumpbotron, but check bs3 first. bs4 does not have a well
				styleTestEl.className = 'jumbotron';
				let styles = getTestStyles();

				let res = styles.marginBottom && styles.paddingBottom
					? true
					: false;

				styleTestEl.__bs4 = res;
			}

			return styleTestEl.__bs4;
		}
	},
	bs5(type) {
		if (type === 'js') {
			return win.bootstrap && win.bootstrap.Modal
				? true
				: false;
		}
		else { // type === 'css'
			if (styleTestEl.__bs5 === undefined) {
				// fs-* classes were added in Bootstrap 5
				styleTestEl.className = 'fs-1';
				let styles = getTestStyles();

				let res = styles.fontSize > 30
					? true
					: false;

				styleTestEl.__bs5 = res;
			}

			return styleTestEl.__bs5;
		}
	},
	ct(type) {
		return false;
	},
	date(type) {
		// JS check only
		return libs.dt(type) && win.jQuery.fn.dtDateTime;
	},
	dt(type) {
		// JS check only
		if (! libs.jq(type)) {
			return false;
		}
		if (! win.jQuery.fn.dataTable) {
			return false;
		}
		if (! $.fn.dataTable.versionCheck) {
			return false;
		}
		if (! $.fn.dataTable.versionCheck('2.0.0')) {
			if (! dtWarningShown) {
				console.warn('CloudTables loading DataTables 2 to replace ' + ($.fn.dataTable as any).version);
				dtWarningShown = true;
			}
			return false;
		}
		return true;
	},
	e(type) {
		// JS check only
		return libs.dt(type) && win.jQuery.fn.dataTable.Editor;
	},
	fh(type) {
		// JS check only
		return libs.dt(type) && win.jQuery.fn.dataTable.FixedHeader;
	},
	jq(type) {
		// JS check only
		return win.jQuery !== undefined;
	},
	jszip(type) {
		// JS check only
		return win.JSZip !== undefined;
	},
	ju(type) {
		if (type === 'js') {
			return win.jQuery && win.jQuery.ui;
		}
		else { // css
			if (styleTestEl.__jui === undefined) {
				// We need ui-dialog for Editor, and jUI can be built without it, so make sure we have it
				styleTestEl.className = 'ui-dialog ui-front';
				let styles = getTestStyles();

				let res = styles.paddingBottom && styles.zIndex > 0
					? true
					: false;

				styleTestEl.__jui = res;
			}

			return styleTestEl.__jui;
		}
	},
	moment(type) {
		// JS check only
		return win.moment !== undefined;
	},
	no(type) {
		return true;
	},
	pdfmake(type) {
		// JS check only
		return win.pdfmake !== undefined;
	},
	pickr(type) {
		// JS check only
		return win.Pickr !== undefined;
	},
	r(type) {
		// JS check only
		return libs.dt(type) && win.jQuery.fn.dataTable.Responsive;
	},
	sb(type) {
		// JS check only
		return libs.dt(type) && win.jQuery.fn.dataTable.SearchBuilder;
	},
	se(type) {
		if (type === 'js') {
			return win.jQuery &&
					win.jQuery.fn.transition &&
					win.jQuery.fn.transition.settings &&
					win.jQuery.fn.transition.settings.name === 'Transition'
				? true
				: false;
		}
		else { // type === 'css'
			if (styleTestEl.__seUI === undefined) {
				// Message element is fairly easy to detect
				styleTestEl.className = 'ui message';
				let styles = getTestStyles();

				let res = styles.position === 'relative' && styles.minHeight && styles.paddingBottom
					? true
					: false;

				styleTestEl.__seUI = res;
			}

			return styleTestEl.__seUI;
		}
	},
	sl(type) {
		// JS check only
		return libs.dt(type) && win.jQuery.fn.dataTable.Select;
	},
	so(type) { // Socket IO
		// JS check only
		return win.io !== undefined;
	},
	sp(type) {
		// JS check only
		return libs.dt(type) && win.jQuery.fn.dataTable.SearchPanes;
	},
	trumbowyg(type) {
		// JS check only
		return libs.jq(type) && win.jQuery.fn.trumbowyg;
	},
	zf(type) {
		if (type === 'js') {
			return win.jQuery && win.jQuery.fn.foundation;
		}
		else { // css
			if (styleTestEl.__foundation === undefined) {
				styleTestEl.className = 'callout warning';
				let styles = getTestStyles();

				let res =
					styles.position === 'relative' &&
					styles.marginBottom &&
					styles.paddingBottom &&
					styles.backgroundColor
						? true
						: false;

				styleTestEl.__foundation = res;
			}

			return styleTestEl.__foundation;
		}
	},
};

/* * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * *
 * Local functions
 */

function getTestStyles() {
	document.body.appendChild(styleTestEl);

	// As soon as the remove is run, the computed styles restore to an empty state, so
	// we need to cache the values that we are interested in. Any extra need to be added
	// here.
	let styles = win.getComputedStyle(styleTestEl);
	let ret = {
		backgroundColor: styles.backgroundColor,
		borderTopWidth: parseInt(styles.borderTopWidth, 10),
		display: styles.display,
		fontSize: parseInt(styles.fontSize, 10),
		marginBottom: parseInt(styles.marginBottom, 10),
		minHeight: parseInt(styles.minHeight, 10),
		paddingBottom: parseInt(styles.paddingBottom, 10),
		position: styles.position,
		zIndex: parseInt(styles.zIndex, 10),
	};

	document.body.removeChild(styleTestEl);

	return ret;
}

function loadCss(path) {
	let n = document.createElement('link');
	n.type = 'text/css';
	n.rel = 'stylesheet';
	n.href = path;

	document.getElementsByTagName('head')[0].appendChild(n);
}

function loadJs(path, insertAfter = null) {
	let n = document.createElement('script');
	n.setAttribute('src', path);
	n.async = false;

	if (insertAfter) {
		// With `nextSibling` this is basically an insertAfter
		insertAfter.parentNode.insertBefore(n, insertAfter.nextSibling);
	}
	else {
		document.head.appendChild(n);
	}
}

// Check if a library is already present on the page
function libPresent(abbr, type: 'css' | 'js') {
	// Use the library's `present` function to determine if we need it
	if (! libs[ abbr ]) {
		console.error('Unknown module: ' + abbr);
	}
	return libs[ abbr ](type);
}

function runStyle(ds: IQueue, map: Map<string, boolean>, type: 'css' | 'js'): void {
	let style = ds.stylingLibs._action;

	// Styling
	if (style === 'auto') {
		// Need to attempt to figure out which one we want - use the 'present' functions to find which one is `true`
		for (let s of ['bs', 'bs4', 'ju', 'se', 'zf']) {
			if (libPresent(s, 'css')) {
				style = s;
				break;
			}
		}

		// Fallback to DataTables plain styling
		if (style === 'auto') {
			style = 'dt';
		}
	}

	// Check for conflict with already loaded tables and styles
	if (win.__ct_style && win.__ct_style !== style) {
		style = win.__ct_style;

		if (! warningShown) {
			warningShown = true;

			console.warn(`CloudTables warning:
Please note that it is not possible to load tables with different styling frameworks on the same page. Using '${style}' styling for all tables on the page, as that was the first one loaded.`);
		}
	}
	else {
		win.__ct_style = style;
	}

	// Determine if we need to load the styling library, or just the DT
	// integration for it. Note that this could happen multiple times on a
	// page (multiple CTs with different features). The style lib itself
	// will only be requested once, but we always need its base name
	let lib = style === 'dt' || libPresent(style, type)
		? style
		: style + '-' + ds.stylingLibs[style];

	map.set(lib, true);
	requested[type].set(lib, true);
}

function runLibs(ds: IQueue, map: Map<string, boolean>, type: 'css' | 'js'): void {
	for (let [abbr, version] of Object.entries(ds.requiredLibs)) {
		let lib = abbr + '-' + version;

		// Only add to the request if it hasn't already been requested and we need it!
		if (! requested[type].has(lib) && ! libPresent(abbr, type)) {
			map.set(lib, true);

			requested[type].set(lib, true);
		}
	}
}

function queueRunner() {
	let queue = win.__ct_datasets;
	let loadUrl = '';

	if (queue) {
		queue = queue.slice();
	}

	// Reset the queue since we are going to process what is already present
	win.__ct_datasets = [];

	let loadCssMap = new Map<string, boolean>();
	let loadJsMap = new Map<string, boolean>();

	for (let ds of queue) {
		// Styling per dataset
		runStyle(ds, loadCssMap, 'css');
		runStyle(ds, loadJsMap, 'js');

		// Add the libraries needed for the dataset (passing in the map to dedup)
		runLibs(ds, loadCssMap, 'css');
		runLibs(ds, loadJsMap, 'js');

		loadUrl = ds.loadUrl;
	}

	// Got list of libs - make single request to get all required for this page
	let libCssNames = Array.from(loadCssMap.keys());
	let libJsNames = Array.from(loadJsMap.keys());

	// Min length possible is 1 since the styling must always be present. It doesn't
	// mean that anything will actually be loaded though
	if (libCssNames.length > 1) {
		// No style request for no styling!
		if (libCssNames[0] !== 'no') {
			loadCss(loadUrl + '/cdn/' + libCssNames.join('/') + '/cloudtables.min.css');
		}
	}

	if (libJsNames.length > 1) {
		loadJs(loadUrl + '/cdn/' + libJsNames.join('/') + '/cloudtables.min.js');
	}

	// Edge CSS / JS
	loadCss(loadUrl + '/css/ct-edge.min.css?edge-1715167034517-dev');
	loadJs(loadUrl + '/js/ct-edge.min.js?edge-1715167034517-dev');

	for (let ds of queue) {
		// Request run file for each DT
		let access = '&token=' + ds.accessToken;

		if (ds.apiKey) {
			access = '&apikey=' + ds.apiKey;

			if (ds.clientId) {
				access += '&clientId=' + ds.clientId;
			}

			if (ds.clientName) {
				access += '&clientName=' + ds.clientName;
			}

			if (ds.condition) {
				access += '&condition=' + ds.condition;
			}

			if (ds.role) {
				access += '&role=' + ds.role;
			}
		}


		if (ds.language) {
			access += '&lang=' + ds.language;
		}

		let insertPoint = null;

		if (ds.insertPoint) {
			let rnd;

			// If given as a string, then an element already has this data attribute set, so we can just reuse it
			if (typeof ds.insertPoint === 'string') {
				rnd = ds.insertPoint;
				insertPoint = document.querySelector('[data-ct-insert=' + ds.insertPoint + ']');
			}
			else {
				rnd = new Date().getTime() + Math.ceil(Math.random() * 1000);
				ds.insertPoint.setAttribute('data-ct-insert', rnd.toString());
				insertPoint = ds.insertPoint;
			}

			access += '&insert=' + rnd;
		}

		let origin = window.location.origin;
		if (origin === 'null') { // firefox
			origin = 'file://';
		}

		let id = '&id=' + ds.tableId;

		loadJs(
			ds.loadUrl + '/dataset/' + ds.datasetId + '/' + ds.viewType +
				'?referer=' + encodeURIComponent(origin + window.location.pathname) +
				access + id,
			insertPoint
		);
	}
}

/* * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * *
 * Interfaces
 */

win.__ct_bootstrap = {
	run: queueRunner
};

// Initial pass
queueRunner();
